import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import { cloudApi } from "../../api";
import "./PharmacyBranches.css";

const ENV = process.env.REACT_APP_ENV || "production";
const IS_DESKTOP = ENV === "desktop";
const NETWORK_TIMEOUT_MS = 10000; // 10 seconds

const PharmacyBranches = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [backendMessage, setBackendMessage] = useState("");
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const navigate = useNavigate();

  // ── Online/offline listener ──
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const fetchBranches = async () => {
      // ── Desktop offline: skip cloud fetch entirely ──
      // cloudApi always hits cloud — on desktop with no internet
      // it will hang indefinitely. Show offline screen instead.
      if (IS_DESKTOP && !navigator.onLine) {
        setLoading(false);
        return;
      }

      try {
        // ── Web: race fetch against timeout ──
        // Prevents endless loading spinner if network drops mid-request.
        const fetchPromise = cloudApi.get("/users/pharmacies/branches");

        const response = IS_DESKTOP
          ? await fetchPromise
          : await Promise.race([
              fetchPromise,
              new Promise((_, reject) =>
                setTimeout(
                  () => reject(new Error("NETWORK_TIMEOUT")),
                  NETWORK_TIMEOUT_MS
                )
              ),
            ]);

        setBranches(response.data.branches || []);
        setBackendMessage(response.data.message || "");
      } catch (err) {
        console.error("Failed to fetch branches:", err);

        if (err.message === "NETWORK_TIMEOUT") {
          setError(
            "⚠️ Network is slow or unavailable. Please check your connection and try again."
          );
        } else if (
          !navigator.onLine ||
          err.code === "ERR_NETWORK" ||
          err.message === "Network Error"
        ) {
          setError("No internet connection. Please connect and try again.");
        } else {
          setError("Failed to load branches. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBranches();
  }, []);

  const filteredBranches = branches.filter(
    (branch) =>
      branch.branch_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      branch.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      branch.branch_address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleBranchClick = (branchId) => {
    navigate(`/pharmacy/branch/${branchId}`);
  };

  // ── Full-page loading screen ──
  if (loading) {
    return (
      <div className="pharmacy-page-container">
        <Sidebar />
        <div
          className="pharmacy-content"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
            gap: "1rem",
          }}
        >
          <div
            style={{
              width: "44px",
              height: "44px",
              border: "4px solid #f0f0f0",
              borderTop: "4px solid #007bff",
              borderRadius: "50%",
              animation: "spin 0.9s linear infinite",
            }}
          />
          <p style={{ color: "#555", fontSize: "1rem", margin: 0, fontWeight: "500" }}>
            Loading branches...
          </p>
          <p style={{ color: "#999", fontSize: "0.85rem", margin: 0 }}>
            Fetching your pharmacy branches
          </p>
        </div>
      </div>
    );
  }

  // ── Desktop offline screen ──
  // Branches always require cloud — can't show anything offline on desktop.
  if (IS_DESKTOP && !isOnline && branches.length === 0) {
    return (
      <div className="pharmacy-page-container">
        <Sidebar />
        <div
          className="pharmacy-content"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60vh",
            gap: "1rem",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "3.5rem" }}>📡</div>
          <h3 style={{ color: "#333", margin: 0 }}>No Internet Connection</h3>
          <p style={{ color: "#666", maxWidth: "360px", lineHeight: 1.6, margin: 0 }}>
            Branch management requires an internet connection. Please connect
            and try again.
          </p>
          <button
            onClick={() => {
              if (navigator.onLine) {
                setIsOnline(true);
                window.location.reload();
              } else {
                setError("Still no internet connection.");
              }
            }}
            style={{
              padding: "0.7rem 1.75rem",
              background: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "6px",
              fontSize: "0.95rem",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            🔄 Retry
          </button>
          {error && (
            <p style={{ color: "#dc3545", fontSize: "0.85rem", margin: 0 }}>
              {error}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="pharmacy-page-container">
      <Sidebar />
      <div className="pharmacy-content">
        <h2 className="pharmacy-title">Pharmacy Branches</h2>

        {/* ── Offline banner — desktop, shown after branches loaded then went offline ── */}
        {IS_DESKTOP && !isOnline && (
          <div style={{
            background: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "8px",
            padding: "0.75rem 1rem",
            marginBottom: "1rem",
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            fontSize: "0.9rem",
          }}>
            <span style={{ fontSize: "1.3rem" }}>📡</span>
            <div>
              <strong style={{ color: "#856404" }}>You are offline</strong>
              <p style={{ margin: 0, color: "#856404", fontSize: "0.85rem" }}>
                Showing cached branch data. Changes require an internet connection.
              </p>
            </div>
          </div>
        )}

        {/* ── Network error banner — shown after timeout or error ── */}
        {error && (
          <div style={{
            background: "#fff5f5",
            border: "1px solid #dc3545",
            borderRadius: "8px",
            padding: "0.75rem 1rem",
            marginBottom: "1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "0.75rem",
            fontSize: "0.9rem",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <span style={{ fontSize: "1.3rem" }}>⚠️</span>
              <p style={{ margin: 0, color: "#dc3545" }}>{error}</p>
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "0.4rem 1rem",
                background: "#dc3545",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                fontSize: "0.85rem",
                whiteSpace: "nowrap",
              }}
            >
              🔄 Retry
            </button>
          </div>
        )}

        <input
          type="text"
          placeholder="🔍 Search branches..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />

        {!error && filteredBranches.length === 0 ? (
          <p className="no-branches-text">
            {backendMessage || "No matching branches found."}
          </p>
        ) : !error && (
          <div className="pharmacy-cards">
            {filteredBranches.map((branch, index) => (
              <div
                key={branch.id}
                className="pharmacy-card branch-clickable"
                onClick={() => handleBranchClick(branch.id)}
              >
                <h3 className="branch-number">Branch {index + 1}</h3>
                <p><strong>Branch Name:</strong> {branch.branch_name}</p>
                <p><strong>User Name:</strong> {branch.user_name}</p>
                <p><strong>Address:</strong> {branch.branch_address}</p>
                {branch.verification_status && (
                  <p>
                    <strong>Status:</strong>{" "}
                    <span
                      className={
                        branch.verification_status === "VERIFIED"
                          ? "verified-status"
                          : "pending-status"
                      }
                    >
                      {branch.verification_status}
                    </span>
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PharmacyBranches;